import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './FacebookFeedSection.css'
import { FB_POSTS } from './fbFeedData'

const AUTOPLAY_MS = 4500
const FB_ICON_PATH =
  'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'

function getVisible() {
  if (typeof window === 'undefined') return 3
  if (window.innerWidth >= 1024) return 3
  if (window.innerWidth >= 640) return 2
  return 1
}

function FbPostCard({ post }) {
  return (
    <div className="fb-slide">
      <div className="fb-card-inner">
        <div className="fb-card-header">
          <div className="fb-avatar">R</div>
          <div>
            <div className="fb-page-name">Astrologer Raju Maharaj Ji</div>
            <div className="fb-page-sub">Vedic Astrologer · Public Figure</div>
          </div>
          <div className="fb-verified">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
        </div>
        <p className="fb-post-text">{post.text}</p>
        <div className="fb-post-img">
          <img src={post.img} alt={post.alt} loading="lazy" />
        </div>
        <div className="fb-reactions">
          <span className="fb-react">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#4285F4" stroke="none">
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3m7 11v-3.343a2 2 0 0 0-.586-1.414l-4.828-4.829A2 2 0 0 1 12 8V5a2 2 0 0 1 2-2 2 2 0 0 1 2 2l-.001 3H20a2 2 0 0 1 2 2l-1 5a2 2 0 0 1-2 2H14Z" />
            </svg>
            {post.likes}
          </span>
          <span className="fb-react">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(28,10,0,0.40)" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {post.comments}
          </span>
          <span className="fb-react">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(28,10,0,0.40)" strokeWidth="2">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            {post.shares}
          </span>
          <span className="fb-date">{post.date}</span>
        </div>
      </div>
    </div>
  )
}

export default function FacebookFeedSection() {
  const sectionRef = useRef(null)
  const trackRef = useRef(null)
  const timerRef = useRef(null)
  const pausedRef = useRef(false)
  const touchStartRef = useRef(0)

  const [visible, setVisible] = useState(getVisible)
  const [current, setCurrent] = useState(0)
  const [slideW, setSlideW] = useState(0)
  const [entered, setEntered] = useState(false)

  const total = FB_POSTS.length
  const maxIdx = Math.max(0, total - visible)

  useLayoutEffect(() => {
    const measure = () => {
      const el = trackRef.current?.children?.[0]
      if (el) setSlideW(el.getBoundingClientRect().width)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [visible])

  useEffect(() => {
    const onResize = () => setVisible(getVisible())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setCurrent((c) => Math.min(c, maxIdx))
  }, [maxIdx])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    if (pausedRef.current) return
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c >= maxIdx ? 0 : c + 1))
    }, AUTOPLAY_MS)
  }, [stop, maxIdx])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (idx) => setCurrent(Math.max(0, Math.min(maxIdx, idx)))

  const next = () => {
    goTo(current >= maxIdx ? 0 : current + 1)
    stop()
    start()
  }
  const prev = () => {
    goTo(current <= 0 ? maxIdx : current - 1)
    stop()
    start()
  }

  const handleMouseEnter = () => {
    pausedRef.current = true
    stop()
  }
  const handleMouseLeave = () => {
    pausedRef.current = false
    start()
  }
  const handleTouchStart = (e) => {
    touchStartRef.current = e.changedTouches[0].clientX
  }
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartRef.current
    if (Math.abs(dx) > 40) {
      if (dx < 0) next()
      else prev()
    }
  }

  return (
    <section id="fb-feed" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.04 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="fb-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#1877F2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#fb-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 50% at 20% 50%,rgba(24,119,242,0.07) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 40% 45% at 90% 80%,rgba(180,160,100,0.06) 0%,transparent 60%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(24,119,242,0.22),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(24,119,242,0.13),transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4" style={{ marginBottom: '40px' }}>
          <div className={`fb-in${entered ? ' fb-on' : ''}`} style={{ animationDelay: '.04s' }}>
            <div className="fb-platform-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2">
                <path d={FB_ICON_PATH} />
              </svg>
              <span>Facebook Feed</span>
            </div>
            <h2 className="fb-heading">
              Follow Us on <span>Facebook</span>
            </h2>
            <p className="fb-sub">Stay connected — latest updates, wisdom &amp; announcements from Astrologer Raju Maharaj Ji.</p>
          </div>
          <a href="#" className={`fb-follow-btn fb-in${entered ? ' fb-on' : ''}`} style={{ animationDelay: '.10s' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2">
              <path d={FB_ICON_PATH} />
            </svg>
            Follow Page
          </a>
        </div>

        <div
          className={`fb-viewport fb-in${entered ? ' fb-on' : ''}`}
          style={{ animationDelay: '.14s' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div ref={trackRef} className="fb-track" style={{ transform: `translateX(${-(current * slideW)}px)` }}>
            {FB_POSTS.map((post) => (
              <FbPostCard post={post} key={post.id} />
            ))}
          </div>
        </div>

        <div className="fb-dots">
          {Array.from({ length: maxIdx + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`fb-dot${i === current ? ' fb-dot-active' : ''}`}
              style={{ width: i === current ? '22px' : '6px' }}
              onClick={() => {
                goTo(i)
                stop()
                start()
              }}
              aria-label={`Go to Facebook post page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
